import { z } from "zod3";
import { fieldError } from "./common";
import { locales } from "@/config";


export type BecomePartnerFormValues = z.output<typeof BecomePartnerFormTs>;


const vatBeTs = z.string()
  .min(12, fieldError.invalidFormat)
  .max(20, fieldError.invalidFormat)
  .regex(/^[\dA-Za-z\s]+$/, fieldError.invalidFormat)
  .transform(val => val.replace(/\s+/g, "").toUpperCase())
  .refine(isValidVatBe, fieldError.invalidFormat);


const siretTs = z.string()
  .min(14, fieldError.invalidFormat)
  .max(20, fieldError.invalidFormat)
  .regex(/^[\d\s]+$/, fieldError.invalidFormat)
  .transform(val => val.replace(/\s+/g, ""))
  .refine(isValidSiret, fieldError.invalidFormat);


export const qtyTs = z.enum([
  "_0",
  "_10",
  "_25",
  "_50",
]).default("_0");


export const BecomePartnerFormTs = z.object({
  locale: z.enum(locales),


  /* step 1
   * ------------------------------------------------------------------------ */

  volume: z.enum([
    "_max_5_per_year",
    "_1_to_2_per_month",
    "_3_to_10_per_month",
    "_more_than_10_per_month",
  ], { message: fieldError.invalidValue }),

  posCount: z.enum([
    "_1_pos",
    "_2_to_5_pos",
    "_more_than_5_pos",
  ], { message: fieldError.invalidValue }),


  /* step 2
   * ------------------------------------------------------------------------ */

  fullName: z.string()
    .min(1, fieldError.required)
    .min(2, fieldError.invalidValue)
    .max(50, fieldError.invalidValue),

  email: z.string()
    .email(fieldError.invalidFormat)
    .max(100, fieldError.invalidFormat),

  phone: z.string()
    .min(5, fieldError.invalidFormat)
    .max(30, fieldError.invalidFormat)
    .regex(/^[+() 0-9\.-]+$/, fieldError.invalidFormat),

  company: z.string()
    .min(1, fieldError.required)
    .min(2, fieldError.invalidValue)
    .max(50, fieldError.invalidValue),


  /* step 3
   * ------------------------------------------------------------------------ */

  country: z.enum([
    "BE",
    "FR",
    //"UK",
    //"ES",
    //"_other",
  ], { message: fieldError.invalidValue }),

  storeName: z.string()
    .min(1, fieldError.invalidValue),

  taxIdSiret: siretTs
    .optional(),

  taxIdVatBe: vatBeTs
    .optional(),

  isSelfContact: z.boolean()
    .default(false),

  contactName: z.string()
    .min(1, fieldError.required)
    .min(2, fieldError.invalidValue)
    .max(50, fieldError.invalidValue)
    .optional(),

  contactEmail: z.string()
    .max(100, fieldError.invalidFormat)
    .email(fieldError.invalidFormat)
    .optional(),

  contactPhone: z.string()
    .min(5, fieldError.invalidFormat)
    .max(30, fieldError.invalidFormat)
    .max(30, fieldError.invalidFormat)
    .regex(/^[+() 0-9\.-]*$/, fieldError.invalidFormat)
    .optional(),


  /* step 4
   * ------------------------------------------------------------------------ */

  skipMaterial: z.boolean().default(false),

  leafletFr: qtyTs,
  leafletEn: qtyTs,
  stickerBlack: qtyTs,
  stickerYellow: qtyTs,

  deliveryStreet: z.string().optional(),
  deliveryNumber: z.string().optional(),
  deliveryExtra: z.string().optional(),
  deliveryZip: z.string().optional(),
  deliveryCity: z.string().optional(),
  altContact: z.boolean().default(false),
  altName: z.string().optional(),
  altEmail: z.string().email(fieldError.invalidFormat).optional(),
  altPhone: z.string().optional(),
});


function isValidVatBe(raw: string): boolean {
  const vat = raw.replace(/\s+/g, "").toUpperCase();
  return /^BE\d{10}$/.test(vat);
}


function isValidSiret(raw: string): boolean {
  const siret = raw.replace(/\s+/g, "");

  if (!/^\d{14}$/.test(siret)) {
    return false;
  }

  let sum = 0;

  for (let i = 0; i < 14; i++) {
    let digit = parseInt(siret.charAt(i), 10);
    if (i % 2 === 0) digit = digit * 2;
    if (digit > 9) digit = digit - 9;
    sum += digit;
  }

  return sum % 10 === 0;
}
